import * as React from "react";
import { ActionType } from "../../../../../react-core/Flux/ActionType";
import { CommonHelper } from "../../../../../react-core/Helpers/CommonHelper";
import { BaseComponent } from "../../../../../react-core/Html/BaseComponent";
import { SelectEditor } from "../../../../../react-core/Html/SelectEditor";
import { DividendReinvestment } from "../../../../../Wrap/Constants/Enums/Domain/DividendReinvestment";
import { InvestmentType } from "../../../../../Wrap/Constants/Enums/InvestmentType";
import { ManagedFundsRebateType } from "../../../../../Wrap/Constants/Enums/ManagedFundsRebateType";
import { Investment } from "../../../../../Wrap/Models/Models/PortfolioAnalysis/Investment/Investment";
import { InvestmentSelectionAction } from "../../../../Shared/IST/Action/InvestmentSelectionAction";
import { FavouritesToggle } from "../../../../Shared/IST/Components/Shared/FavouritesToggle";
import { FundType } from "../../../../Shared/IST/Resource/Content";
import { Tab } from "../../../../Shared/Tab/Tab";
import { Table } from "../../../../Shared/Table/Table";
import { InvestmentDetailsPopupModal } from "../../Clients/Common/InvestmentDetailsPopUp/InvestmentDetailsPopupModal";
import { FundDataType, ManagedFundsRebateTypeMap } from "../Resource/Content";
import { ResearchISTStore } from "../Store/Variable";
import { uniqBy } from "lodash";
import { DepositProductType } from "../../../../../Wrap/Constants/Enums/Domain/DepositProductType";
import { MoneyDisplayHelper } from "../../../../../react-core/Helpers/MoneyDisplayHelper";
import { CurrencyCode } from "../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { DepositProduct } from "../../../../../Wrap/Models/Domains/Investment/DepositProduct";
import { DepositDetailsPopupModal } from "../../Clients/Common/DepositDetailsPopUp/DepositDetailsPopupModal";
import AriaTooltip from "../../../../Shared/Tooltip/AriaTooltip";

interface ICashDepositResearchResultTableProps {
    tab: InvestmentType;
}

interface ICashDepositResearchResultTableState {
    data: any;
    fundCode: any;
    exchangeTradeCode: any;
    curPage: number;
    selectedInvestment: DepositProduct;
    isShowDetailModal: boolean;
    currentFundDataType: FundDataType;
}

export class CashDepositResearchResultTable extends BaseComponent<ICashDepositResearchResultTableProps, ICashDepositResearchResultTableState> {
    constructor(props: ICashDepositResearchResultTableProps) {
        super(props);
        this.state = {
            data: null,
            fundCode: { key: "ProductCode", value: "Fund code" },
            exchangeTradeCode: { key: "ProductCode", value: "Code" },
            curPage: 1,
            isShowDetailModal: false,
            selectedInvestment: null,
            currentFundDataType: FundDataType.Overview,
        };
        this.subscribeToStore(ResearchISTStore, () => this.onStoreChange());
    }

    private onStoreChange() {
        if (ResearchISTStore.getActionType() == ActionType.Success) {
            this.setState({ data: ResearchISTStore.getData() });
            if (ResearchISTStore.getIsBasicSearch()) {
                this.setState({ curPage: 1 });
            }
        }
        if (ResearchISTStore.getActionType() == ActionType.Update) {
            this.setState({ curPage: 1 });
        }
    }

    private codeFundOptions = [
        { key: "ProductCode", value: "Fund code" },
        { key: "Isin", value: "ISIN" },
        { key: "Sedol", value: "SEDOL" },
        { key: "MexId", value: "Mex ID" },
    ];


    private renderInvestmentDetailPopupModal = (): JSX.Element => {
        if (!this.state.isShowDetailModal) {return null;}
        return (
            <>
                <DepositDetailsPopupModal
                    showBox={this.state.isShowDetailModal}
                    callBack={() => {
                        this.setState({ isShowDetailModal: false });
                    }}
                    instrumentCode={this.state.selectedInvestment?.ProductCode}
                    productId={this.state.selectedInvestment?.ProductId}
                    issuer={this.state.selectedInvestment?.Issuer}
                    depositType={this.state.selectedInvestment?.DepositProductType}
                    term={this.state.selectedInvestment?.Term}
                    grossRate={this.state.selectedInvestment?.GrossInterestRate}
                    aerRate={this.state.selectedInvestment?.AnnualEquivalentInterestRate}
                    minimumInvestment={this.state.selectedInvestment?.MinimumInvestment}
                    maximumInvestment={this.state.selectedInvestment?.MaximumInvestment}
                    instrumentName={this.state.selectedInvestment?.Name}
                />
            </>
        );
    };

    private onInvestmentSelected = (depositProduct) => {
        this.setState({ selectedInvestment: depositProduct, isShowDetailModal: true });
    };

    private getInterestRateHeader(): JSX.Element
    {
        return (
            <>
                <div>
                    AER | Gross
                    <AriaTooltip cssWrapper="is-info-icon">
                        {
                            <small>AER stands for Annual Equivalent Rate. The AER illustrates what the interest rate would be if interest was paid and compounded once each year. It helps you compare interest rates across different products.  <strong>Note:</strong> As part of our range of deposit takers, we have Sharia products which offer an expected profit rate, rather than interest on their savings products, in order to comply with Sharia banking principles.</small>
                        }
                    </AriaTooltip>
                </div>
            </>
        );
    }

    private getTable = () => {
        const { fundCode, currentFundDataType } = this.state;

        const result = [];

        result.push(
            {
                title: "Provider",
                key: "Name",

                render: (rowData, row, index) => {
                    return (
                        <a className="hyperlink" onClick={() => this.onInvestmentSelected(row)}>
                            {rowData}
                        </a>
                    );
                },
            },
            {
                title: "Type",
                key: "DepositProductType",
                render: (rowData, row, index) => {
                    switch(rowData) {
                        case DepositProductType.EasyAccess:
                            return <>Easy access</>
                        case DepositProductType.Notice:
                            return <>Notice account</>;
                        case DepositProductType.Term:
                            return <>Fixed term</>
                    }
                }
            },
            {
                title: this.getInterestRateHeader(),
                key: "GrossInterestRate",
                render: (rowData, row, index) => {
                    return (
                        <>{MoneyDisplayHelper.getPercentageFormat(row.AnnualEquivalentInterestRate, 2)} | {MoneyDisplayHelper.getPercentageFormat(rowData, 2)}</>
                    );
                },
             },
            {
                title: "Term / Notice",
                key: "Term",
                render: (rowData, row, index) => {
                    switch(row.DepositProductType) {
                        case DepositProductType.EasyAccess:
                            return <>N/A</>
                        case DepositProductType.Notice:
                            return <>{rowData} days</>;
                        case DepositProductType.Term:
                            if (rowData == 12) {
                                return <>1 year</>
                            } else if (rowData == 1) {
                                return <>1 month</>
                            } else if (rowData % 12 == 0) {
                                return <>{rowData / 12} years</>
                            } else {
                                return <>{rowData} months</>
                            }
                    }
                },
            },
            {
                title: "Min opening amount",
                key: "MinimumInvestment",
                render: (rowData, row, index) => {
                    return (
                        <>{MoneyDisplayHelper.FromatMoneyDisplay(rowData, CurrencyCode.GBP, true, 0)}</>
                    );
                },
            },
            {
                title: "Interest paid",
                key: "Type",
                render: (rowData, row, index) => {
                    switch(row.DepositProductType) {
                        case DepositProductType.EasyAccess:
                        case DepositProductType.Notice:
                            return <>Daily</>;
                        case DepositProductType.Term:
                            return <>Maturity</>;
                    }
                }
            },
        );
        return result;
    };

    render() {
        const { data, curPage } = this.state;
        if (!data) {return null;}
        return (
            <div className="panel pr-0 pl-0 pb-0 pt-0 mt-40">
                <h3>Selection</h3>
                <p>
                    {"Cash deposits available: "}
                    {data.Pager ? data.Pager.TotalRecords : data.length}
                </p>

                <Table
                    data={
                        data.Results
                            ? uniqBy(data.Results, "ProductId")
                            : uniqBy(data, "ProductId").slice((curPage - 1) * 10, curPage * 10)
                    }
                    cssWrapper={"no-sided-padding has-favourite-toggle"}
                    showEmptyMsg
                    cssEmptyMessage="warning-message mt-20"
                    needPagination={{
                        totalNumOfResult: data.Pager ? data.Pager.TotalRecords : uniqBy(data, "ProductId").length,
                        currentPage: curPage,
                        onPaginationChange: (pageIndex) => {
                            if (data.Pager) {
                                this.setState({ curPage: pageIndex });
                                ResearchISTStore.search(pageIndex);
                            } else {
                                this.setState({ curPage: pageIndex });
                            }
                        },
                    }}
                    col={this.getTable()}
                />
                {this.renderInvestmentDetailPopupModal()}
            </div>
        );
    }
}
